import React, { useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

/** Sherlock's catchphrase — shown in the hover tooltip and on the mobile flyout. */
export const SHERLOCK_GREETING = "I'm Sherlock Holmes and I will help you find things";

interface SherlockIconProps {
  sx?: SxProps<Theme>;
  ariaLabel?: string;
  /** Disable mouse-tracking; useful for tests or static contexts. */
  trackMouse?: boolean;
  /**
   * Show a hover tooltip introducing Sherlock. Pass `true` for the default
   * catchphrase, or a string for custom text. Defaults to off so the
   * decorative empty-state icons don't pop tooltips on hover.
   */
  tooltip?: boolean | string;
}

/**
 * Cartoon Sherlock Holmes (chest-up: deerstalker, mustache, pipe) with a
 * magnifying glass that follows the cursor along the X axis:
 *   - mouse far left of icon  → glass at his left hand
 *   - mouse roughly centered  → glass held up in front of his face
 *   - mouse far right of icon → glass at his right hand
 *
 * Sizes via `font-size` (SVG width/height = `1em`), so callers can keep
 * passing `sx={{ fontSize: 48 }}` exactly like an MUI icon. Line work uses
 * `currentColor`, so the figure inherits the parent's text color.
 *
 * Mouse tracking is global (single listener per instance, rAF-batched) and
 * is disabled on touch-only devices and when `prefers-reduced-motion: reduce`.
 */
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const SherlockIcon: React.FC<SherlockIconProps> = ({
  sx,
  ariaLabel = 'search',
  trackMouse = true,
  tooltip = false,
}) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const glassRef = useRef<SVGGElement>(null);
  const handleLeftRef = useRef<SVGPathElement>(null);
  const handleRightRef = useRef<SVGPathElement>(null);
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);

  // Live-track the reduced-motion preference so the CSS transition turns off
  // immediately if the user toggles it at the OS level.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (!trackMouse) return;
    if (typeof window === 'undefined') return;

    // Skip tracking on touch-only devices.
    if (window.matchMedia('(hover: none)').matches) return;

    let rafId: number | null = null;
    let mouseX = 0;
    let mouseY = 0;
    let hovered = false;

    const apply = () => {
      rafId = null;
      const wrapper = wrapperRef.current;
      const glass = glassRef.current;
      if (!wrapper || !glass) return;

      const rect = wrapper.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;

      // Normalize horizontal mouse offset to [-1, 1] over a half-window radius.
      // Past the radius the glass parks at the corresponding hand.
      const radius = Math.max(window.innerWidth / 2, 240);
      const t = Math.max(-1, Math.min(1, (mouseX - cx) / radius));

      // Glass travels ±18 SVG units around the chest center (viewBox is 64×64).
      const tx = t * 18;

      // Arc motion: at the sides the glass sits low (in his hand); at the
      // center it lifts up to overlap the face — the iconic peering pose.
      const tyArc = -12 * (1 - Math.abs(t));
      const dy = mouseY - cy;
      const tyBob = Math.max(-2, Math.min(2, -dy * 0.008));
      const ty = tyArc + tyBob;

      // "Lean in" — scale glass up when cursor is near the icon.
      const dist = Math.hypot(mouseX - cx, mouseY - cy);
      const proximity = Math.max(0, 1 - dist / 200);
      const scale = 1 + proximity * 0.12 + (hovered ? 0.06 : 0);

      glass.style.transform = `translate(${tx.toFixed(2)}px, ${ty.toFixed(2)}px) scale(${scale.toFixed(3)})`;

      // Crossfade the two handles by side. No handle is visible at the very
      // center — like he's pinching the rim with his thumbs while peering.
      const leftOp = Math.max(0, -t);
      const rightOp = Math.max(0, t);
      if (handleLeftRef.current) handleLeftRef.current.style.opacity = leftOp.toFixed(3);
      if (handleRightRef.current) handleRightRef.current.style.opacity = rightOp.toFixed(3);
    };

    const schedule = () => {
      if (rafId === null) rafId = requestAnimationFrame(apply);
    };

    const onMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      schedule();
    };

    const onEnter = () => { hovered = true; schedule(); };
    const onLeave = () => { hovered = false; schedule(); };

    window.addEventListener('mousemove', onMove, { passive: true });
    const wrapper = wrapperRef.current;
    wrapper?.addEventListener('mouseenter', onEnter);
    wrapper?.addEventListener('mouseleave', onLeave);

    return () => {
      window.removeEventListener('mousemove', onMove);
      wrapper?.removeEventListener('mouseenter', onEnter);
      wrapper?.removeEventListener('mouseleave', onLeave);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [trackMouse]);

  const content = (
    <Box
      component="span"
      ref={wrapperRef}
      role="img"
      aria-label={ariaLabel}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        lineHeight: 1,
        color: 'inherit',
        ...sx,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 64 64"
        sx={{
          width: '1em',
          height: '1em',
          overflow: 'visible',
          display: 'block',
        }}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        {/* ------- Figure (stays still) — head & hat dominate the viewBox ------- */}
        <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          {/* Small collar sliver under the chin */}
          <path
            d="M14 62 C 18 54, 24 52, 32 52 C 40 52, 46 54, 50 62"
            strokeWidth="2"
            fill="rgba(0,0,0,0.06)"
          />
          <path d="M28 53 L 32 58 L 36 53" strokeWidth="1.2" opacity="0.65" />

          {/* Head — larger */}
          <ellipse
            cx="32"
            cy="34"
            rx="14"
            ry="16"
            strokeWidth="2.2"
            fill="rgba(244,201,155,0.95)"
          />

          {/* Deerstalker hat — sized to the bigger head */}
          <path
            d="M17 21
               C 18 9, 26 4, 32 4
               C 38 4, 46 9, 47 21
               L 46 23 L 18 23 Z"
            strokeWidth="2.2"
            fill="rgba(120, 78, 50, 0.95)"
          />
          {/* Hat band */}
          <path d="M18 22 L 46 22" strokeWidth="1.4" opacity="0.6" />
          {/* Side ear flap */}
          <path
            d="M17 21 C 13 22, 12 26, 14 29 L 20 27 Z"
            strokeWidth="1.6"
            fill="rgba(120, 78, 50, 0.95)"
          />
          {/* Front bill */}
          <path
            d="M20 23 L 33 23 L 32 26 L 22 26 Z"
            strokeWidth="1.6"
            fill="rgba(95, 60, 38, 0.95)"
          />
          {/* Plaid hint */}
          <path d="M25 7 L 25 21" strokeWidth="0.8" opacity="0.45" />
          <path d="M39 7 L 39 21" strokeWidth="0.8" opacity="0.45" />

          {/* Eyes */}
          <circle cx="27" cy="32" r="1.3" fill="currentColor" stroke="none" />
          <circle cx="37" cy="32" r="1.3" fill="currentColor" stroke="none" />

          {/* Mustache */}
          <path
            d="M24 42 C 27 45, 30 45, 32 42 C 34 45, 37 45, 40 42"
            strokeWidth="1.8"
            fill="rgba(60, 40, 22, 0.95)"
          />

          {/* Pipe — stem out of right side of mouth, bowl curling down */}
          <path
            d="M40 44 L 49 45 C 51 45, 52 47, 51 49"
            strokeWidth="1.8"
          />
          {/* Smoke curls */}
          <path
            d="M51 45 C 53 43, 53 41, 52 40"
            strokeWidth="1.2"
            opacity="0.55"
          />
          <path
            d="M54 42 C 55.5 40.5, 55.5 38.5, 54.5 37.5"
            strokeWidth="1"
            opacity="0.4"
          />
        </g>

        {/* ------- Magnifying glass (moves with cursor; overlaps face when centered) ------- */}
        <g
          ref={glassRef}
          style={{
            transformOrigin: '32px 44px',
            transformBox: 'view-box',
            transition: reducedMotion ? 'none' : 'transform 220ms cubic-bezier(.22,.61,.36,1)',
            willChange: 'transform',
          }}
        >
          {/* Handle pointing down-left — visible when the glass is on the left side */}
          <path
            ref={handleLeftRef}
            d="M25 51 L 18 58"
            stroke="rgba(80, 50, 28, 0.95)"
            strokeWidth="2.4"
            strokeLinecap="round"
            style={{
              opacity: 0,
              transition: reducedMotion ? 'none' : 'opacity 220ms ease-out',
            }}
          />
          {/* Handle pointing down-right — visible when the glass is on the right side */}
          <path
            ref={handleRightRef}
            d="M39 51 L 46 58"
            stroke="rgba(80, 50, 28, 0.95)"
            strokeWidth="2.4"
            strokeLinecap="round"
            style={{
              opacity: 1,
              transition: reducedMotion ? 'none' : 'opacity 220ms ease-out',
            }}
          />
          {/* Lens ring — larger; semi-transparent fill so the face shows through */}
          <circle
            cx="32"
            cy="44"
            r="10"
            fill="rgba(180, 220, 240, 0.4)"
            stroke="currentColor"
            strokeWidth="2"
          />
          {/* Lens highlight */}
          <path
            d="M26 39 C 28 37, 30 36.5, 32 36.5"
            stroke="white"
            strokeWidth="1.4"
            fill="none"
            strokeLinecap="round"
            opacity="0.85"
          />
        </g>
      </Box>
    </Box>
  );

  if (!tooltip) return content;
  const title = tooltip === true ? SHERLOCK_GREETING : tooltip;
  return (
    <Tooltip title={title} arrow placement="bottom" enterDelay={250} enterNextDelay={250}>
      {/* span wrapper so the Tooltip can attach its ref/handlers without
          colliding with the icon's internal wrapperRef used for mouse-tracking */}
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{content}</span>
    </Tooltip>
  );
};

export default SherlockIcon;
